// The door lock screen (DeviceIDs 3 and 102) — originally a port of Flutter's
// doorScreen.dart, a 4-digit keypad checked against the LOGGED-IN USER'S
// PASSWORD (not a per-device code).
//
// ⚠️ THE PHONE CHECKS FIRST (2026-09-29, screen lock added 2026-10-13). On a
// phone with a fingerprint, or with a pattern / PIN / password screen lock, the
// keypad is replaced by one Unlock button that opens the phone's own prompt
// (core/auth/biometricGate) — fingerprint, with "use pattern" offered beside it.
// Only where the phone has neither — or the plugin is missing — does the old
// app keypad come back. The same check guards creating a guest password on 102.
//
// A verified user publishes the unlock command — OPENDOOR on 3, LOCKUNLOCK on
// 102, via deviceMeta.unlockCommand. The confirmation says the command was SENT;
// the lock's own reply is what says it opened.
//
// ⚠️ NO EXTRA TAPS (owner, 2026-10-20). On the Main Door Lock (3) the phone's
// fingerprint prompt opens BY ITSELF as the screen opens — the round "Tap to
// unlock" button is only for a retry after Cancel. And "Unlock sent" is a toast
// that goes back to the room on its own; it used to be a dialog with an OK.
// The Room Door Lock (102) keeps its explicit button: its screen is also where
// guest passwords are managed, so opening it must not mean opening the door.
//
// The Room Door Lock (102) also shows its battery (core/devices/lockBattery,
// asked with GETBATTERY on open) and its guest passwords (components/LockPasswords).
//
// This is a client-side gate on a convenience action, not a security boundary —
// the device itself is what authorises the command.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, TextField, useTheme,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PatternIcon from '@mui/icons-material/Pattern';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import BatteryUnknownIcon from '@mui/icons-material/BatteryUnknown';
import { useNavigate, useLocation } from 'react-router-dom';
import DeviceAppBar from './components/DeviceAppBar';
import { useApp } from '../../../../core/state/AppContext';
import { publishCommand } from '../../../../core/utils/deviceController';
import { unlockCommand, unlockLabel, ROOM_DOOR_LOCK_ID } from '../../../../core/devices/deviceMeta';
import { supportsLockPasswords, getLockState, openRoomDoorLockViaServer } from '../../../../core/devices/lockPasswords';
import { supportsBattery, parseBattery, CMD_GET_BATTERY } from '../../../../core/devices/lockBattery';
import { useDeviceStatus } from '../../../../core/mqtt/useDeviceStatus';
import {
  isUnlockRequest, unlockRequestSecondsLeft, UNLOCK_REQUEST_WINDOW_MS,
} from '../../../../core/devices/lockEvents';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  unlockMethod, verifyBiometric, pinMatches, usesPhonePrompt,
} from '../../../../core/auth/biometricGate';
import LockPasswords from './components/LockPasswords';
import { showToast } from '../../../shared/toast';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '<'];

/** Battery line for 102 — the lock's own words, or "unknown" until it answers. */
function BatteryLine({ battery, c }) {
  if (!battery) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: `${c.text}99` }}>
        <BatteryUnknownIcon fontSize="small" />
        <Typography sx={{ fontSize: 13 }}>Battery: asking the lock…</Typography>
      </Box>
    );
  }
  if (battery.unknown) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: `${c.text}99` }}>
        <BatteryUnknownIcon fontSize="small" />
        <Typography sx={{ fontSize: 13 }}>Battery: not reported yet</Typography>
      </Box>
    );
  }
  const colour = battery.low ? '#e53935' : c.text;
  // A STORED value (server, lockEvents.js) says when it is from; a live one does not need to.
  const asOf = battery.at
    ? ` (as of ${new Date(battery.at).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})`
    : '';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: colour }}>
      {battery.low ? <BatteryAlertIcon fontSize="small" /> : <BatteryFullIcon fontSize="small" />}
      <Typography sx={{ fontSize: 14, fontWeight: battery.low ? 700 : 400 }}>
        {`Battery ${battery.percent}%${battery.low ? ' — LOW, replace soon' : ''}${asOf}`}
      </Typography>
    </Box>
  );
}

export default function DoorScreen({ device }) {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { user, userId, realUserId } = useApp();
  const uno = device?.UniqueNumber;
  const deviceId = device?.DeviceID;
  // 102 has an explicit "Open Room Door Lock" button (sends LOCKUNLOCK): the PIN
  // is typed, THEN the button is pressed — never sent on the fourth digit.
  const explicitButton = Number(deviceId) === ROOM_DOOR_LOCK_ID;

  const [method, setMethod] = useState(null);          // null checking | 'biometric' | 'device' | 'pin'
  const [pin, setPin] = useState('');
  const [wrong, setWrong] = useState(false);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    unlockMethod().then((m) => { if (alive) setMethod(m); });
    return () => { alive = false; };
  }, []);

  // ── Battery (102) ─────────────────────────────────────────────────────────
  const hasBattery = supportsBattery(deviceId);
  const [status, , liveAt] = useDeviceStatus(hasBattery ? uno : null);
  const [battery, setBattery] = useState(null);
  // Someone at the lock asking to be let in (DP 9 — core/devices/lockEvents).
  const [request, setRequest] = useState(null);   // { at, left } | null
  const [, setTick] = useState(0);
  useEffect(() => {
    const b = parseBattery(status);
    if (b) setBattery(b);
    if (isUnlockRequest(status)) setRequest((r) => ({ at: Date.now(), left: r ? r.left : null }));
    const left = unlockRequestSecondsLeft(status);
    if (left != null) setRequest({ at: Date.now(), left });
  // liveAt too: the lock repeats the SAME text, which alone would not re-run this.
  }, [status, liveAt]);
  const requestActive = !!request && Date.now() - request.at < UNLOCK_REQUEST_WINDOW_MS
    && request.left !== 0;
  useEffect(() => {
    if (!request) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [request]);
  useEffect(() => {
    if (!hasBattery || !uno) return undefined;
    publishCommand(uno, CMD_GET_BATTERY, userId);
    // The last value the server heard, for a lock that is asleep right now. A
    // live answer replaces it (and never the other way round).
    let alive = true;
    getLockState(uno).then((st) => {
      if (!alive || !st || st.battery == null) return;
      setBattery((live) => live || { percent: st.battery, low: !!st.batteryLow, at: st.batteryAt });
    });
    return () => { alive = false; };
  }, [hasBattery, uno, userId]);

  // 102 opens THROUGH THE SERVER (/api/OpenRoomDoorLock checks this account is
  // mapped to the lock, then publishes LOCKUNLOCK). Only when the server cannot
  // be reached does the app publish it itself; a refusal is final. Lock 3 is
  // unchanged: OPENDOOR straight over MQTT.
  const unlock = async () => {
    if (Number(deviceId) === ROOM_DOOR_LOCK_ID) {
      const r = await openRoomDoorLockViaServer(uno, realUserId || userId);
      if (r === 'refused') { setNotice('This account is not allowed to open this lock.'); return; }
      if (r === 'unavailable') publishCommand(uno, unlockCommand(deviceId), userId);
    } else {
      publishCommand(uno, unlockCommand(deviceId), userId);
    }
    showToast('Unlock sent to the lock', { severity: 'success' });
    navigate(-1);
  };

  // ── PIN fallback, as a dialog, for actions other than the keypad itself ───
  // (creating a guest password on a phone without a fingerprint).
  const pinResolver = useRef(null);
  const [pinAsk, setPinAsk] = useState(false);
  const [pinEntry, setPinEntry] = useState('');
  const askPin = () => new Promise((resolve) => {
    pinResolver.current = resolve;
    setPinEntry('');
    setPinAsk(true);
  });
  const answerPin = (ok) => {
    setPinAsk(false);
    const r = pinResolver.current;
    pinResolver.current = null;
    if (r) r(ok);
  };

  /** One identity check for every lock action on this screen. -> boolean */
  const confirmIdentity = useCallback(async (reason) => {
    if (usesPhonePrompt(method)) {
      const r = await verifyBiometric(reason);
      if (!r.ok && r.message) setNotice(r.message);
      return r.ok;
    }
    return askPin();
  }, [method]);

  const letThemIn = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await confirmIdentity('Open the door for the person at the lock');
    setBusy(false);
    if (ok) { setRequest(null); unlock(); }
  };

  // Arrived from the "Someone is at the door" notification's OPEN button
  // (?unlock=1, core/push/registerPush): ask for the fingerprint straight away —
  // once, as soon as the check method is known — then send LOCKUNLOCK. The query
  // is dropped first so Back, or a re-render, can never ask again.
  const { search, pathname, state: navState } = useLocation();
  const autoOpenDone = useRef(false);
  useEffect(() => {
    if (autoOpenDone.current || method === null) return;
    if (new URLSearchParams(search).get('unlock') !== '1') return;
    autoOpenDone.current = true;
    navigate(pathname, { replace: true, state: navState });
    letThemIn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, search]);

  const fingerprintUnlock = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await confirmIdentity(`Unlock ${device?.DeviceName || 'the door'}`);
    setBusy(false);
    if (ok) unlock();
  };

  // Lock 3: the fingerprint prompt as the screen opens — once, never again after
  // a Cancel (the round button is the retry). Not when the notification flow
  // above already asked, and never on 102 (see the header).
  const autoPromptDone = useRef(false);
  useEffect(() => {
    if (autoPromptDone.current || explicitButton || !usesPhonePrompt(method)) return;
    if (new URLSearchParams(search).get('unlock') === '1') return;
    autoPromptDone.current = true;
    fingerprintUnlock();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  const press = (key) => {
    if (key === '<') { setPin((p) => p.slice(0, -1)); return; }
    if (pin.length >= 4) return;

    const next = pin + key;
    setPin(next);
    if (next.length < 4 || explicitButton) return;

    if (pinMatches(next, user)) {
      setPin('');
      unlock();
    } else {
      setPin('');
      setWrong(true);
    }
  };

  // The keypad's button (102): the 4 digits typed, then pressed.
  const submitPin = () => {
    if (pin.length !== 4) return;
    if (pinMatches(pin, user)) { setPin(''); unlock(); } else { setPin(''); setWrong(true); }
  };

  const openButton = (onClick, disabled) => (
    <Button
      variant="contained"
      size="large"
      startIcon={<LockOpenIcon />}
      disabled={disabled}
      onClick={onClick}
      sx={{ mt: 2, px: 4, py: 1.5, borderRadius: 999, fontSize: 17, textTransform: 'none' }}
    >
      {unlockLabel(deviceId)}
    </Button>
  );

  const panelWidth = Math.min(window.innerWidth, 420);

  const keypad = (
    <>
      <Typography sx={{ fontSize: 26, color: c.text, fontWeight: 500 }}>
        Enter Passcode
      </Typography>

      <Box sx={{ height: 30 }} />

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              mx: 1.25,
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: `1px solid ${c.text}`,
              bgcolor: i < pin.length ? c.text : 'transparent',
            }}
          />
        ))}
      </Box>

      <Box sx={{ height: 50 }} />

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        p: 5,
      }}
      >
        {KEYS.map((key, i) => (key === '' ? (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={`gap-${i}`} />
        ) : (
          <Box
            key={key}
            onClick={() => press(key)}
            sx={{
              m: 1,
              aspectRatio: '1',
              borderRadius: '50%',
              border: `1px solid ${c.text}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {key === '<' ? (
              <BackspaceIcon sx={{ color: c.text }} />
            ) : (
              <Typography sx={{ color: c.text, fontSize: 28, fontWeight: 500 }}>
                {key}
              </Typography>
            )}
          </Box>
        )))}
      </Box>
      {explicitButton && openButton(submitPin, pin.length !== 4)}
    </>
  );

  const fingerprint = (
    <Box sx={{ py: 4 }}>
      <Box
        role="button"
        aria-label={method === 'device' ? 'Unlock with screen lock' : 'Unlock with fingerprint'}
        onClick={fingerprintUnlock}
        sx={{
          mx: 'auto', width: 150, height: 150, borderRadius: '50%',
          border: `2px solid ${c.text}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: busy ? 'default' : 'pointer', userSelect: 'none', opacity: busy ? 0.6 : 1,
          '&:active': { filter: 'brightness(1.3)' },
        }}
      >
        {busy
          ? <CircularProgress sx={{ color: c.text }} />
          : method === 'device'
            ? <PatternIcon sx={{ fontSize: 96, color: c.text }} />
            : <FingerprintIcon sx={{ fontSize: 96, color: c.text }} />}
      </Box>
      {explicitButton ? (
        openButton(fingerprintUnlock, busy)
      ) : (
        <Typography sx={{ fontSize: 22, color: c.text, fontWeight: 500, mt: 3 }}>
          Tap to unlock
        </Typography>
      )}
      <Typography sx={{ fontSize: 13, color: `${c.text}99`, mt: 1 }}>
        {method === 'device'
          ? 'Your phone’s pattern, PIN or password confirms it is you.'
          : 'Your fingerprint — or your phone’s pattern / PIN — confirms it is you.'}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} subtitle={device?.LocationName ?? ''} />

      <Box sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
      }}
      >
        <Box sx={{ width: '100%', maxWidth: panelWidth, textAlign: 'center' }}>
          {hasBattery && <Box sx={{ mb: 2 }}><BatteryLine battery={battery} c={c} /></Box>}
          {requestActive && (
            <Box sx={{
              mb: 2, p: 1.5, borderRadius: 2, bgcolor: '#FFB30022', border: '1px solid #FFB300',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: c.text }}>
                <NotificationsActiveIcon sx={{ color: '#FFB300' }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                  {`Someone is asking to open the door${request.left != null ? ` — ${request.left}s` : ''}`}
                </Typography>
              </Box>
              {openButton(letThemIn, busy)}
            </Box>
          )}
          {/* Nothing while the (at most 2 s) check runs — no spinner. */}
          {usesPhonePrompt(method) && fingerprint}
          {method === 'pin' && keypad}
        </Box>
      </Box>

      {/* Room Door Lock (102): one-time / time-limited / scheduled guest codes,
          behind the same identity check as Unlock. */}
      {supportsLockPasswords(deviceId) && method !== null
        ? <LockPasswords device={device} verify={confirmIdentity} />
        : null}

      <Snackbar
        open={wrong}
        autoHideDuration={1000}
        onClose={() => setWrong(false)}
        message="Incorrect PIN"
      />
      <Snackbar
        open={!!notice}
        autoHideDuration={2500}
        onClose={() => setNotice('')}
        message={notice}
      />

      {/* TOP of the screen, not centred: the on-screen keyboard covers the lower
          half, and centred the Confirm / Cancel buttons sat underneath it. */}
      <Dialog
        open={pinAsk}
        onClose={() => answerPin(false)}
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{ sx: { bgcolor: c.back, mt: 'calc(env(safe-area-inset-top) + 24px)' } }}
      >
        <DialogTitle sx={{ color: c.text }}>Enter your 4-digit PIN</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="password"
            value={pinEntry}
            onChange={(e) => setPinEntry(e.target.value.replace(/\D/g, '').slice(0, 4))}
            inputProps={{ inputMode: 'numeric', style: { color: c.text, letterSpacing: 8, textAlign: 'center', fontSize: 24 } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => answerPin(false)} sx={{ color: c.text }}>Cancel</Button>
          <Button
            disabled={pinEntry.length !== 4}
            onClick={() => {
              const ok = pinMatches(pinEntry, user);
              if (!ok) setWrong(true);
              answerPin(ok);
            }}
            sx={{ color: c.button }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
